import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth";
import { useOwnerIdentity } from "@/lib/useOwnerIdentity";
import { useUserOrganizations } from "@/lib/org";

// Phase 6.12.2 - Owner auto-link (mirrors useTenantAutoLink).
// An authenticated user who is NOT yet a linked owner, is NOT an org member,
// and carries an owner_client_id in their auth metadata (from the invite) is
// linked here on first load by calling the owner-portal 'link' action.
//
// RACE FIX (6.12.2 acceptance testing): inFlight previously rose only inside
// the effect, one commit AFTER RoleHome had already rendered <Navigate> on
// the render where the last identity query settled. Whichever hook's identity
// query settled last lost the race deterministically and the link never
// fired before navigation. The hold condition (`eligible && !settled`) is now
// DERIVED DURING RENDER from the same conditions the effect uses, so RoleHome
// holds the loading screen in the very render where the queries settle,
// before any effect runs. The effect then performs the attempt and releases
// the hold when it fully resolves (identity refetched on success).

export function useOwnerAutoLink() {
  const { user, loading: authLoading } = useAuth();
  const { data: owner, isSuccess: ownerLoaded } = useOwnerIdentity();
  const { data: memberships, isSuccess: orgsLoaded } = useUserOrganizations();
  const queryClient = useQueryClient();
  const [inFlight, setInFlight] = useState(false);
  const [settled, setSettled] = useState(false);
  const attempted = useRef(false);

  const ownerClientId = (user?.user_metadata as Record<string, unknown> | undefined)
    ?.owner_client_id;

  // Computed at render time. True means: a link attempt is warranted and its
  // outcome is not yet known, so RoleHome must not route.
  const eligible =
    !authLoading &&
    !!user &&
    ownerLoaded &&
    orgsLoaded &&
    !owner &&                                  // not already linked
    (memberships?.length ?? 0) === 0 &&        // a manager, not an owner
    typeof ownerClientId === "string" &&
    ownerClientId.length > 0;

  useEffect(() => {
    if (!eligible) return;
    if (attempted.current) return;             // once per mount
    attempted.current = true;
    setInFlight(true);

    (async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData.session?.access_token;
        if (!token) return;
        const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/owner-portal`;
        await fetch(url, {
          method: "POST",
          headers: {
            "content-type": "application/json",
            authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ action: "link" }),
        });
      } catch {
        // Network/transient errors: fall through to the refetch below.
      } finally {
        // Refetch identity REGARDLESS of link outcome, before releasing the
        // hold. The cached identity may be stale from a prior session in the
        // same window (sign-out clears the Supabase session, not the React
        // Query cache), and an already-linked user gets a non-2xx from the
        // no-takeover guard while their identity row is perfectly valid.
        await queryClient.invalidateQueries({ queryKey: ["owner-identity"] });
        setInFlight(false);
        setSettled(true);
      }
    })();
  }, [eligible, queryClient]);

  // Hold from the first eligible render until the attempt fully resolves.
  return { inFlight: inFlight || (eligible && !settled) };
}
